import {
  AlertTriangle,
  Ban,
  Check,
  ChevronDown,
  CircleAlert,
  Clock3,
  Download,
  ExternalLink,
  FileText,
  FlaskConical,
  Globe2,
  LoaderCircle,
  ShieldAlert,
  ShieldCheck,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type {
  ArtifactDetail,
  ArtifactRecord,
  EvidenceRecord,
  PermissionRequest,
  SseEvent,
} from '../api';
import { recordFrom, stringFrom, stringList } from '../api';
import { eventDuration, eventWarnings, formatBytes, formatDate, formatDuration } from '../utils';
import { MarkdownReport } from './MarkdownReport';

type InspectorTab = 'execution' | 'evidence' | 'artifacts';
type ExecutionStatus = 'running' | 'done' | 'error' | 'cancelled';
type TrustStatus = EvidenceRecord['status'] | 'pending';

interface InspectorProps {
  events: SseEvent[];
  evidence: EvidenceRecord[];
  artifacts: ArtifactRecord[];
  permissions: PermissionRequest[];
  externalNetworkUsed?: boolean;
  warnings: string[];
  streamLabel: string;
  narrowOpen: boolean;
  loadArtifact(artifact: ArtifactRecord): Promise<ArtifactDetail>;
  artifactHref(artifact: ArtifactRecord): string;
  onPermission(request: PermissionRequest, decision: 'approve' | 'deny'): void;
  onCloseNarrow(): void;
}

export function Inspector({
  events,
  evidence,
  artifacts,
  permissions,
  externalNetworkUsed,
  warnings,
  streamLabel,
  narrowOpen,
  loadArtifact,
  artifactHref,
  onPermission,
  onCloseNarrow,
}: InspectorProps) {
  const [tab, setTab] = useState<InspectorTab>('execution');
  const executions = useMemo(() => buildExecutionItems(events), [events]);
  const trustStatus: TrustStatus =
    evidence.length === 0
      ? 'pending'
      : evidence.some((item) => item.status === 'quarantined')
        ? 'quarantined'
        : evidence.every((item) => item.status === 'verified')
          ? 'verified'
          : 'unverified';
  const primaryEvidence = evidence[0];
  const annotation = recordFrom(primaryEvidence?.raw.annotation);
  const demo = annotation.demo === true || primaryEvidence?.origin === 'demo';
  const localFirst = annotation.local_first === true || primaryEvidence?.origin === 'local';

  return (
    <aside className={`inspector ${narrowOpen ? 'is-narrow-open' : ''}`} aria-label="研究检查器">
      <div className="inspector-heading">
        <div className="inspector-heading-copy">
          <strong>研究检查器</strong>
          <span>追踪本次研究的执行过程、证据来源与产出</span>
          <span>
            {streamLabel} · 外部网络 {externalNetworkUsed === undefined ? '未知' : externalNetworkUsed ? '已使用' : '未使用'}
          </span>
          <div className="trust-strip" aria-label="结论可信状态">
            <span className={`trust-chip ${trustStatus}`}>{evidenceStatusLabel(trustStatus)}</span>
            {primaryEvidence?.confidence !== undefined && (
              <span className="trust-chip neutral">置信 {primaryEvidence.confidence}</span>
            )}
            <span className={`trust-chip ${externalNetworkUsed ? 'network' : 'neutral'}`}>
              {externalNetworkUsed ? '已联网' : '本机运行'}
            </span>
            {demo && <span className="trust-chip neutral">演示数据</span>}
            {localFirst && <span className="trust-chip neutral">本地优先</span>}
          </div>
        </div>
        <button className="icon-button inspector-narrow-close" type="button" onClick={onCloseNarrow} aria-label="关闭检查器">
          <X size={17} />
        </button>
      </div>

      <div className="inspector-tabs" role="tablist" aria-label="检查器视图">
        <TabButton active={tab === 'execution'} onClick={() => setTab('execution')} label="执行" count={executions.length + permissions.length} />
        <TabButton active={tab === 'evidence'} onClick={() => setTab('evidence')} label="证据" count={evidence.length} />
        <TabButton active={tab === 'artifacts'} onClick={() => setTab('artifacts')} label="产物" count={artifacts.length} />
      </div>

      <div className="inspector-content">
        {tab === 'execution' && (
          <ExecutionPanel executions={executions} permissions={permissions} onPermission={onPermission} />
        )}
        {tab === 'evidence' && (
          <EvidencePanel
            evidence={evidence}
            externalNetworkUsed={externalNetworkUsed}
            warnings={warnings}
          />
        )}
        {tab === 'artifacts' && (
          <ArtifactPanel artifacts={artifacts} loadArtifact={loadArtifact} artifactHref={artifactHref} />
        )}
      </div>
    </aside>
  );
}

function TabButton({ active, onClick, label, count }: { active: boolean; onClick(): void; label: string; count: number }) {
  return (
    <button type="button" role="tab" aria-selected={active} className={active ? 'is-active' : ''} onClick={onClick}>
      {label}
      <span>{count}</span>
    </button>
  );
}

interface ExecutionItem {
  id: string;
  name: string;
  status: ExecutionStatus;
  summary?: string;
  target?: string;
  duration?: number;
  ts?: number;
  warnings: string[];
  type: string;
  seq: number;
}

function ExecutionPanel({
  executions,
  permissions,
  onPermission,
}: {
  executions: ExecutionItem[];
  permissions: PermissionRequest[];
  onPermission(request: PermissionRequest, decision: 'approve' | 'deny'): void;
}) {
  if (!executions.length && !permissions.length) {
    return <EmptyInspector title="暂无执行事件" detail="工具调用和审批请求会在执行期间显示。" />;
  }
  return (
    <div className="inspector-stack">
      {permissions.map((request) => (
        <section className="permission-card" key={request.requestId}>
          <div className="card-heading">
            <span className="status-icon warning"><ShieldAlert size={16} /></span>
            <div>
              <strong>需要权限确认</strong>
              <small>请求 {request.requestId}</small>
            </div>
          </div>
          <dl>
            <div><dt>目标</dt><dd>{request.target}</dd></div>
            <div><dt>原因</dt><dd>{request.reason}</dd></div>
          </dl>
          <div className="permission-actions">
            <button className="secondary-button" type="button" onClick={() => onPermission(request, 'deny')}>
              <Ban size={15} />拒绝
            </button>
            <button className="primary-button" type="button" onClick={() => onPermission(request, 'approve')}>
              <Check size={15} />批准
            </button>
          </div>
        </section>
      ))}

      {executions.map((item) => (
        <section className={`execution-card status-${item.status}`} key={item.id}>
          <div className="card-heading">
            <ExecutionIcon status={item.status} />
            <div>
              <strong>{item.name}</strong>
              <small>{executionStatusLabel(item.status)} · {typeLabel(item.type)}</small>
            </div>
            {item.duration !== undefined && <span className="duration"><Clock3 size={12} />{formatDuration(item.duration)}</span>}
          </div>
          {item.target && <div className="execution-target">{item.target}</div>}
          {item.summary && (
            <details>
              <summary>执行说明 <ChevronDown size={14} /></summary>
              <p>{item.summary}</p>
            </details>
          )}
          {item.warnings.map((warning) => (
            <div className="inline-warning" key={warning}><AlertTriangle size={14} />{warning}</div>
          ))}
          {item.ts && <time>{formatDate(item.ts)}</time>}
        </section>
      ))}
    </div>
  );
}

function EvidencePanel({
  evidence,
  externalNetworkUsed,
  warnings,
}: {
  evidence: EvidenceRecord[];
  externalNetworkUsed?: boolean;
  warnings: string[];
}) {
  const counts = {
    verified: evidence.filter((item) => item.status === 'verified').length,
    unverified: evidence.filter((item) => item.status === 'unverified').length,
    quarantined: evidence.filter((item) => item.status === 'quarantined').length,
  };
  return (
    <div className="inspector-stack evidence-panel">
      <div className="evidence-statusbar" aria-label="证据状态汇总">
        <span className="verified"><ShieldCheck size={14} />已验证 {counts.verified}</span>
        <span className="unverified"><CircleAlert size={14} />未验证 {counts.unverified}</span>
        <span className="quarantined"><XCircle size={14} />已隔离 {counts.quarantined}</span>
      </div>
      <div className={`network-usage ${externalNetworkUsed ? 'used' : ''}`}>
        <Globe2 size={15} />
        <span>
          本次是否联网取证：
          <strong>{externalNetworkUsed === undefined ? '未知' : externalNetworkUsed ? '是' : '否'}</strong>
        </span>
      </div>
      {warnings.map((warning) => (
        <div className="global-warning" key={warning}><AlertTriangle size={15} />{warning}</div>
      ))}
      {!evidence.length && <EmptyInspector title="暂无证据记录" detail="证据进入会话后会显示验证状态、来源与置信说明。" />}
      {evidence.map((item) => (
        <section className={`evidence-card evidence-${item.status}`} key={item.id}>
          <div className="card-heading">
            <EvidenceIcon status={item.status} />
            <div>
              <strong>{item.title}</strong>
              <small>{evidenceStatusLabel(item.status)} · {originLabel(item.origin)}</small>
            </div>
          </div>
          {item.summary && <p>{item.summary}</p>}
          {item.source && <div className="evidence-source">来源：{item.source}</div>}
          {(item.confidence !== undefined || item.confidenceExplanation) && (
            <div className="confidence-box">
              <strong>置信说明{item.confidence !== undefined ? ` · ${item.confidence}` : ''}</strong>
              <span>{item.confidenceExplanation ?? '服务端未提供置信解释。'}</span>
            </div>
          )}
          {item.warnings.map((warning) => (
            <div className="inline-warning" key={warning}><AlertTriangle size={14} />{warning}</div>
          ))}
          {item.notes.map((note) => (
            <div className="evidence-note" key={note}>{note}</div>
          ))}
        </section>
      ))}
    </div>
  );
}

function ArtifactPanel({
  artifacts,
  loadArtifact,
  artifactHref,
}: {
  artifacts: ArtifactRecord[];
  loadArtifact(artifact: ArtifactRecord): Promise<ArtifactDetail>;
  artifactHref(artifact: ArtifactRecord): string;
}) {
  const [selected, setSelected] = useState<ArtifactRecord>();
  const [detail, setDetail] = useState<ArtifactDetail>();
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [error, setError] = useState<string>();
  const [blobUrl, setBlobUrl] = useState<string>();

  useEffect(() => () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  const preview = async (artifact: ArtifactRecord) => {
    setSelected(artifact);
    setDetail(undefined);
    setError(undefined);
    setState('loading');
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(undefined);
    }
    try {
      const result = await loadArtifact(artifact);
      setDetail(result);
      if (result.blob) setBlobUrl(URL.createObjectURL(result.blob));
      setState('ready');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '产物预览失败。');
      setState('error');
    }
  };

  if (!artifacts.length) {
    return <EmptyInspector title="暂无产物" detail="报告、表格和图像等产物生成后会出现在这里。" />;
  }

  return (
    <div className="artifact-panel">
      <div className="artifact-list">
        {artifacts.map((artifact) => (
          <div className={`artifact-row ${selected?.id === artifact.id ? 'is-selected' : ''}`} key={artifact.id}>
            <button type="button" onClick={() => void preview(artifact)}>
              <FileText size={16} />
              <span><strong>{artifact.name}</strong><small>{artifact.path || artifact.kind || '产物'}</small></span>
            </button>
            <span className="artifact-size">{formatBytes(artifact.size)}</span>
            <a href={artifact.downloadUrl || artifactHref(artifact)} target="_blank" rel="noreferrer" aria-label={`打开或下载 ${artifact.name}`}>
              <Download size={15} />
            </a>
          </div>
        ))}
      </div>

      {selected && (
        <section className="artifact-preview">
          <header>
            <div><strong>{selected.name}</strong><small>预览来自会话 API</small></div>
            <a href={selected.downloadUrl || artifactHref(selected)} target="_blank" rel="noreferrer">
              <ExternalLink size={14} />打开
            </a>
          </header>
          {state === 'loading' && <div className="panel-state"><LoaderCircle className="spin" size={18} />正在读取产物</div>}
          {state === 'error' && <div className="panel-state error-message">{error}</div>}
          {state === 'ready' && detail?.content && <MarkdownReport content={detail.content} />}
          {state === 'ready' && blobUrl && detail?.mimeType?.startsWith('image/') && <img src={blobUrl} alt={detail.name} />}
          {state === 'ready' && blobUrl && !detail?.mimeType?.startsWith('image/') && (
            <a className="secondary-button artifact-blob-link" href={blobUrl} target="_blank" rel="noreferrer">打开二进制预览</a>
          )}
          {state === 'ready' && !detail?.content && !blobUrl && (
            <div className="panel-state">服务端未返回可内嵌的预览内容，可使用“打开”查看。</div>
          )}
        </section>
      )}
    </div>
  );
}

function EmptyInspector({ title, detail }: { title: string; detail: string }) {
  return <div className="inspector-empty"><FlaskConical size={22} /><strong>{title}</strong><span>{detail}</span></div>;
}

function ExecutionIcon({ status }: { status: ExecutionStatus }) {
  if (status === 'running') return <span className="status-icon running"><LoaderCircle className="spin" size={16} /></span>;
  if (status === 'error') return <span className="status-icon error"><XCircle size={16} /></span>;
  if (status === 'cancelled') return <span className="status-icon warning"><Ban size={16} /></span>;
  return <span className="status-icon done"><Check size={16} /></span>;
}

function EvidenceIcon({ status }: { status: EvidenceRecord['status'] }) {
  if (status === 'verified') return <span className="status-icon verified"><ShieldCheck size={16} /></span>;
  if (status === 'quarantined') return <span className="status-icon quarantined"><XCircle size={16} /></span>;
  return <span className="status-icon unverified"><CircleAlert size={16} /></span>;
}

function buildExecutionItems(events: SseEvent[]): ExecutionItem[] {
  const groups = new Map<string, ExecutionItem>();
  const relevant = events.filter((event) => {
    const type = event.type.toLowerCase();
    if (type.includes('delta') || type.includes('chunk') || type.includes('token')) return false;
    if (type.includes('evidence') || type.includes('artifact')) return false;
    return (
      type.includes('tool') ||
      type.includes('execution') ||
      type.includes('command') ||
      type.includes('step') ||
      type.includes('turn.started') ||
      type.includes('turn.completed') ||
      type.includes('turn.failed') ||
      type.includes('turn.cancelled') ||
      type.includes('turn.canceled')
    );
  });

  for (const event of relevant) {
    const payload = event.payload;
    const type = event.type.toLowerCase();
    const key =
      (type.startsWith('turn.') ? event.turn_id : undefined) ??
      stringFrom(payload.tool_call_id ?? payload.call_id ?? payload.execution_id ?? payload.step_id) ??
      event.event_id ??
      `${event.type}-${event.seq}`;
    const payloadStatus = stringFrom(payload.status ?? payload.state)?.toLowerCase();
    const status: ExecutionStatus =
      type.includes('cancel') || payloadStatus === 'cancelled' || payloadStatus === 'canceled'
        ? 'cancelled'
        : type.includes('error') ||
            type.includes('fail') ||
            payloadStatus === 'failed' ||
            payloadStatus === 'error' ||
            payloadStatus === 'blocked'
          ? 'error'
          : type.includes('completed') ||
              type.includes('done') ||
              type.includes('success') ||
              payloadStatus === 'success' ||
              payloadStatus === 'ok' ||
              payloadStatus === 'completed'
            ? 'done'
            : 'running';
    const existing = groups.get(key);
    const current: ExecutionItem = existing ?? {
      id: key,
      name:
        stringFrom(payload.title ?? payload.tool_name ?? payload.tool ?? payload.action ?? payload.name) ??
        typeLabel(event.type),
      status,
      warnings: [],
      type: event.type,
      seq: event.seq,
    };
    current.status =
      status === 'error' ||
      status === 'cancelled' ||
      (status === 'done' && current.status !== 'error')
        ? status
        : current.status;
    current.summary =
      stringFrom(payload.summary ?? payload.description ?? payload.reason) ??
      stringList(payload.summary ?? payload.description ?? payload.reason).join('；') ??
      current.summary;
    current.target = stringFrom(payload.target ?? payload.command ?? payload.path) ?? current.target;
    current.duration = eventDuration(event) ?? current.duration;
    current.ts = event.ts_ms ?? current.ts;
    current.warnings = Array.from(new Set([...current.warnings, ...eventWarnings(event)]));
    current.type = event.type;
    current.seq = Math.max(current.seq, event.seq);
    groups.set(key, current);
  }
  return Array.from(groups.values()).sort((a, b) => b.seq - a.seq);
}

function executionStatusLabel(status: ExecutionStatus): string {
  if (status === 'running') return '执行中';
  if (status === 'error') return '失败';
  if (status === 'cancelled') return '已停止';
  return '已完成';
}

function evidenceStatusLabel(status: TrustStatus): string {
  if (status === 'pending') return '尚无证据';
  if (status === 'verified') return '已验证';
  if (status === 'quarantined') return '已隔离';
  return '未验证';
}

function originLabel(origin: EvidenceRecord['origin']): string {
  if (origin === 'local') return '本地';
  if (origin === 'network') return '网络';
  if (origin === 'demo') return '演示';
  return '来源未知';
}

function typeLabel(type: string): string {
  const normalized = type.replace(/[._-]+/g, ' ');
  const labels: Record<string, string> = {
    'turn started': '任务开始',
    'turn completed': '任务完成',
    'turn failed': '任务失败',
    'tool started': '工具调用',
    'tool completed': '工具完成',
    'tool failed': '工具失败',
  };
  return labels[normalized.toLowerCase()] ?? normalized;
}
